<template>
  <list-page-shell>
    <template #actions>
      <el-button type="primary" size="small" icon="el-icon-plus" @click="openProductForm(null)">{{ $t('common.create') }}</el-button>
    </template>

    <template #filters>
      <el-form :inline="true" :model="filters" size="small">
        <el-form-item :label="$t('productListPage.keywordLabel')">
          <el-input
            v-model="filters.keyword"
            clearable
            :placeholder="$t('productListPage.keywordPh')"
            style="width:240px;"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        <el-form-item :label="$t('common.status')">
          <el-select v-model="filters.statusCode" clearable :placeholder="$t('productListPage.statusAll')" style="width:140px;">
            <el-option :label="$t('enum.productStatus.1')" :value="1" />
            <el-option :label="$t('enum.productStatus.2')" :value="2" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" :loading="loading" @click="handleSearch">{{ $t('common.search') }}</el-button>
          <el-button icon="el-icon-refresh" :disabled="loading" @click="handleReset">{{ $t('common.reset') }}</el-button>
        </el-form-item>
      </el-form>
    </template>

    <el-table :data="list" border stripe v-loading="loading" style="width:100%;">
      <el-table-column prop="productName" :label="$t('productFormPage.productName')" min-width="180" />
      <el-table-column prop="productCode" :label="$t('productFormPage.productCode')" width="140" />
      <el-table-column :label="$t('productFormPage.unitPrice')" width="140" align="right">
        <template slot-scope="s">{{ formatUsd(s.row.unitPrice) }}</template>
      </el-table-column>
      <el-table-column :label="$t('common.status')" width="110">
        <template slot-scope="s">
          <el-tag :type="Number(s.row.statusCode) === 1 ? 'success' : 'info'" size="mini">
            {{ labelStatus(s.row.statusCode) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="description" :label="$t('productFormPage.description')" min-width="180" show-overflow-tooltip />
      <el-table-column prop="updatedAt" :label="$t('grid.createdAt')" width="160" />
      <el-table-column :label="$t('common.actions')" width="140">
        <template slot-scope="s">
          <el-button type="text" size="small" @click="openProductForm(s.row.id)">{{ $t('common.edit') }}</el-button>
          <el-button type="text" size="small" class="danger" v-permission="['OFFICE','ADMIN']" @click="remove(s.row)">{{ $t('common.delete') }}</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="list-pager">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next"
        :page-sizes="[10, 20, 50]"
        :total="total"
        :page-size="pageSize"
        :current-page="pageNo"
        @size-change="onSizeChange"
        @current-change="p => { pageNo = p; load() }"
      />
    </div>

    <RecordFormDialog :visible.sync="productFormVisible" :title="productFormTitle">
      <ProductForm
        v-if="productFormVisible"
        ref="productFormRef"
        :key="productFormKey"
        embedded
        :record-id="productFormRecordId"
        @saved="onProductFormSaved"
        @saving="v => (productFormSaving = v)"
      />
      <template slot="footer">
        <el-button @click="productFormVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="productFormSaving" @click="submitProductFormDialog">{{ $t('common.save') }}</el-button>
      </template>
    </RecordFormDialog>
  </list-page-shell>
</template>

<script>
import ListPageShell from '@/components/ListPageShell.vue'
import RecordFormDialog from '@/components/RecordFormDialog.vue'
import ProductForm from '@/views/product/ProductForm.vue'
import { deleteProduct, getProductPage } from '@/api/product'
import { formatUsd } from '@/utils/formatUsd'

export default {
  name: 'ProductList',
  components: { ListPageShell, RecordFormDialog, ProductForm },
  data() {
    return {
      productFormVisible: false,
      productFormSaving: false,
      productFormRecordId: null,
      productFormKey: 0,
      list: [],
      total: 0,
      pageNo: 1,
      pageSize: 10,
      loading: false,
      filters: { keyword: '', statusCode: null }
    }
  },
  computed: {
    productFormTitle() {
      return this.productFormRecordId
        ? this.$t('productFormPage.editTitle')
        : this.$t('productFormPage.newTitle')
    }
  },
  created() {
    this.load()
  },
  methods: {
    formatUsd,
    openProductForm(id) {
      this.productFormRecordId = id != null ? id : null
      this.productFormKey += 1
      this.productFormVisible = true
    },
    onProductFormSaved() {
      this.productFormVisible = false
      this.load()
    },
    submitProductFormDialog() {
      if (this.$refs.productFormRef) this.$refs.productFormRef.submit()
    },
    onSizeChange(size) {
      this.pageSize = size
      this.pageNo = 1
      this.load()
    },
    handleSearch() {
      this.pageNo = 1
      this.load()
    },
    handleReset() {
      this.filters = { keyword: '', statusCode: null }
      this.pageNo = 1
      this.load()
    },
    labelStatus(code) {
      const c = code != null ? Number(code) : 1
      return this.$t('enum.productStatus.' + c)
    },
    async load() {
      this.loading = true
      try {
        const params = { pageNo: this.pageNo, pageSize: this.pageSize }
        const kw = (this.filters.keyword || '').trim()
        if (kw) params.keyword = kw
        if (this.filters.statusCode != null && this.filters.statusCode !== '') {
          params.statusCode = this.filters.statusCode
        }
        const r = await getProductPage(params)
        const d = r.data || r
        this.list = d.records || []
        this.total = d.total || 0
      } finally {
        this.loading = false
      }
    },
    async remove(row) {
      await this.$confirm(this.$t('productListPage.confirmDelete'), this.$t('common.tip'), { type: 'warning' })
      await deleteProduct(row.id)
      this.$message.success(this.$t('common.deleted'))
      this.load()
    }
  }
}
</script>

<style scoped>
.list-pager {
  margin-top: 16px;
  text-align: right;
}

.danger {
  color: #f56c6c;
}
</style>
